/* ============================================================
   NOVAA GEAR — Choose Your Vehicle collection page
   - 3-step vehicle picker (Make → Model → Generation)
   - Filter chips (make + status)
   - Sort (popular / newest / a-z)
   - Request-vehicle form
   ============================================================ */

(function () {
  'use strict';
  if (window.__novaaCollectionInit) return;
  window.__novaaCollectionInit = true;

  // -------- VEHICLE PICKER DATA (single source of truth) --------
  // Generations are the "Year" axis per Novaa convention — series codes,
  // not model years. Each entry maps to a /products/<slug> destination.
  //
  // 25 Aug 2026 — Prado 150 added; Ranger repointed from the Dual Cab Ute
  // listing to its own listing (was split against the home + PYV grids);
  // van slugs repointed off the ARCHIVED 'sprinter-van-rear-bar-gear-carrier'
  // (every van in this table was routing to a 404).
  const FITMENT = {
    Toyota: {
      'LandCruiser 200 Series': [
        { gen: 'J200', label: 'J200 · 2007–2021', slug: '200s-lc-hitch-mounted-rear-bar' }
      ],
      'LandCruiser 300 Series': [
        { gen: 'J300', label: 'J300 · 2021 →', slug: '300-series-landcruiser-hitch-mounted-rear-bar-alternative' }
      ],
      'Prado 150 Series': [
        { gen: 'J150', label: 'J150 · 2009–2024', slug: 'toyota-150prado-hitch-mounted-rear-bar' }
      ],
      'Prado 250 Series': [
        { gen: 'J250', label: 'J250 · 2024 →', slug: 'toyota-250prado-hitch-mounted-rear-bar' }
      ],
      'HiLux': [
        { gen: 'N80', label: 'N80 Dual Cab · 2015 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ]
    },
    Ford: {
      'Ranger': [
        { gen: 'P703', label: 'P703 Dual Cab · 2022 →', slug: 'ford-ranger-hitch-mounted-rear-bar' },
        { gen: 'PX', label: 'PX Dual Cab · 2011–2022', slug: 'ford-ranger-hitch-mounted-rear-bar' }
      ],
      'Everest': [
        { gen: 'UB', label: 'UB · 2022 →', slug: 'ford-everest-hitch-mounted-rear-bar-1' }
      ]
    },
    Isuzu: {
      'D-Max': [
        { gen: 'RG', label: 'Dual Cab · 2020 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ],
      'MU-X': [
        { gen: 'RJ', label: '2021 →', slug: 'isuzu-mu-x-hitch-mounted-rear-bar' }
      ]
    },
    Nissan: {
      'Patrol': [
        { gen: 'Y62', label: 'Y62 · 2012 →', slug: 'nissan-patrol-hitch-mounted-rear-bar' }
      ],
      'Navara': [
        { gen: 'NP300', label: 'NP300 Dual Cab · 2015 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ]
    },
    Mazda: {
      'BT-50': [
        { gen: 'TF', label: 'Dual Cab · 2020 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ]
    },
    Mitsubishi: {
      'Triton': [
        { gen: 'MR/MV', label: 'Dual Cab · 2015 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ]
    },
    Volkswagen: {
      'Amarok': [
        { gen: '2H/NF', label: 'Dual Cab · 2011 →', slug: 'dual-cab-ute-hitch-mounted-rear-bar' }
      ]
    },
    Van: {
      'Toyota HiAce': [
        { gen: 'H300', label: 'Small/Medium Van · 2019 →', slug: 'toyota-hiace-hitch-mounted-rear-gear' }
      ],
      'VW Transporter': [
        { gen: 'T6', label: 'Medium Van · 2015 →', slug: 'toyota-hiace-hitch-mounted-rear-gear' }
      ],
      'Mercedes Sprinter': [
        { gen: 'VS30', label: 'Medium/Large Van · 2018 →', slug: 'universal-hitch-mounted-rear-bar' }
      ],
      'Ford Transit': [
        { gen: 'VO', label: 'Medium/Large Van · 2014 →', slug: 'universal-hitch-mounted-rear-bar' }
      ],
      'VW Crafter': [
        { gen: 'SY', label: 'Large Van · 2017 →', slug: 'universal-hitch-mounted-rear-bar' }
      ]
    }
  };

  // -------- PICKER --------
  const stepMake = document.querySelector('[data-step="make"]');
  const stepModel = document.querySelector('[data-step="model"]');
  const stepGen = document.querySelector('[data-step="gen"]');
  const findBtn = document.querySelector('[data-find]');
  const selMake = stepMake?.querySelector('select');
  const selModel = stepModel?.querySelector('select');
  const selGen = stepGen?.querySelector('select');

  function fillSelect(sel, items, placeholder) {
    if (!sel) return;
    sel.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    ph.disabled = true;
    ph.selected = true;
    sel.appendChild(ph);
    items.forEach(item => {
      const opt = document.createElement('option');
      if (typeof item === 'string') {
        opt.value = item; opt.textContent = item;
      } else {
        opt.value = item.value; opt.textContent = item.label;
        if (item.slug) opt.dataset.slug = item.slug;
        if (item.gen)  opt.dataset.gen  = item.gen;
      }
      sel.appendChild(opt);
    });
  }

  function setStepState(step, state) {
    if (!step) return;
    step.classList.remove('is-filled', 'is-disabled', 'is-active');
    if (state) step.classList.add(state);
  }

  function initPicker() {
    if (!selMake) return;
    fillSelect(selMake, Object.keys(FITMENT), 'Select make…');
    setStepState(stepModel, 'is-disabled');
    setStepState(stepGen,   'is-disabled');
    findBtn && (findBtn.disabled = true);

    selMake.addEventListener('change', () => {
      const make = selMake.value;
      setStepState(stepMake, 'is-filled');
      setStepState(stepModel, '');
      setStepState(stepGen,   'is-disabled');
      findBtn.disabled = true;

      const models = make ? Object.keys(FITMENT[make] || {}) : [];
      fillSelect(selModel, models, 'Select model…');
      selModel.disabled = false;
      fillSelect(selGen, [], 'Select series…');
      selGen.disabled = true;
    });

    selModel.addEventListener('change', () => {
      const make = selMake.value, model = selModel.value;
      setStepState(stepModel, 'is-filled');
      setStepState(stepGen, '');
      findBtn.disabled = true;

      const gens = (FITMENT[make] && FITMENT[make][model]) || [];
      fillSelect(selGen, gens.map(g => ({ value: g.gen, label: g.label, slug: g.slug, gen: g.gen })), 'Select series…');
      selGen.disabled = false;
    });

    selGen.addEventListener('change', () => {
      setStepState(stepGen, 'is-filled');
      findBtn.disabled = !selGen.value;
    });

    findBtn.addEventListener('click', () => {
      const opt = selGen.options[selGen.selectedIndex];
      const slug = opt?.dataset.slug;
      if (!slug) return;
      // Route to filtered PDP-style results page (per design)
      const url = '/products/' + slug;
      window.location.href = url;
    });
  }

  // -------- FILTER + SORT --------
  const grid = document.querySelector('[data-vgrid]');
  const filterChips = document.querySelectorAll('[data-filter]');
  const sortSel = document.querySelector('[data-sort]');
  const countLive = document.querySelector('[data-count]');
  const emptyEl = document.querySelector('[data-empty]');

  function applyFilters() {
    if (!grid) return;
    const onChip = document.querySelector('[data-filter].is-on');
    const filter = onChip ? onChip.dataset.filter : 'all';
    const cards = Array.from(grid.querySelectorAll('.vcard:not(.request)'));
    let visible = 0;

    cards.forEach(card => {
      const make = (card.dataset.make || '').toLowerCase();
      const status = (card.dataset.status || 'live').toLowerCase();
      let show = false;
      if (filter === 'all') show = true;
      else if (filter === 'soon') show = (status === 'soon');
      else if (filter === 'universal') show = (make === 'universal');
      else show = (make === filter);
      card.classList.toggle('is-hidden', !show);
      if (show) visible++;
    });

    if (emptyEl) emptyEl.style.display = visible === 0 ? 'block' : 'none';
    if (countLive) countLive.textContent = String(visible);
  }

  function applySort() {
    if (!grid || !sortSel) return;
    const mode = sortSel.value;
    const cards = Array.from(grid.querySelectorAll('.vcard:not(.request)'));
    const request = grid.querySelector('.vcard.request');

    cards.sort((a, b) => {
      if (mode === 'newest') {
        return (+b.dataset.year || 0) - (+a.dataset.year || 0);
      }
      if (mode === 'az') {
        return (a.dataset.sortKey || '').localeCompare(b.dataset.sortKey || '');
      }
      // popular (default)
      return (+b.dataset.popular || 0) - (+a.dataset.popular || 0);
    });
    cards.forEach(c => grid.appendChild(c));
    if (request) grid.appendChild(request);
  }

  filterChips.forEach(chip => {
    chip.addEventListener('click', () => {
      filterChips.forEach(c => c.classList.remove('is-on'));
      chip.classList.add('is-on');
      applyFilters();
    });
  });

  if (sortSel) {
    sortSel.addEventListener('change', () => { applySort(); applyFilters(); });
  }

  // -------- REQUEST FORM --------
  const reqWrap = document.querySelector('[data-request]');
  if (reqWrap) {
    const form = reqWrap.querySelector('form');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      reqWrap.classList.add('is-submitted');
    });
  }

  // boot
  let booted = false;
  function boot() { if (booted) return; booted = true; initPicker(); applySort(); applyFilters(); }
  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();
})();
